import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { 
  Building2, Zap, Droplets, AlertTriangle, CheckCircle2, 
  TrendingUp, MapPin, Wrench, Home
} from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InfrastructureDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // State for all data
  const [stateOverview, setStateOverview] = useState(null);
  const [buildingOwnership, setBuildingOwnership] = useState(null);
  const [dilapidationData, setDilapidationData] = useState(null);
  const [amenitiesData, setAmenitiesData] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [priorityRanking, setPriorityRanking] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      const [overview, ownership, dilapidation, amenities, heatmap, ranking] = await Promise.all([
        axios.get(`${API_URL}/api/dashboards/infrastructure/state-overview`),
        axios.get(`${API_URL}/api/dashboards/infrastructure/building-ownership`),
        axios.get(`${API_URL}/api/dashboards/infrastructure/dilapidation-analysis`),
        axios.get(`${API_URL}/api/dashboards/infrastructure/amenities-coverage`),
        axios.get(`${API_URL}/api/dashboards/infrastructure/infrastructure-heatmap`),
        axios.get(`${API_URL}/api/dashboards/infrastructure/district-priority-ranking`)
      ]);

      setStateOverview(overview.data);
      setBuildingOwnership(ownership.data);
      setDilapidationData(dilapidation.data);
      setAmenitiesData(amenities.data);
      setHeatmapData(heatmap.data);
      setPriorityRanking(ranking.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading infrastructure data:', error);
      setLoading(false);
    }
  };

  // Reusable KPI Card Component
  const KPICard = ({ title, value, unit, icon: Icon, color, subtitle, trend }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {trend && (
            <div className={`text-sm font-semibold ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? '↑' : '↓'}
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold" style={{ color }}>{value}{unit}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading Infrastructure Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PAGE 1: State Overview
  const OverviewTab = () => {
    if (!stateOverview) return <div>No data available</div>;

    const buildingTypeData = [
      { name: 'Own Building', value: stateOverview.building_ownership.own_building, fill: '#22c55e' },
      { name: 'Rented', value: stateOverview.building_ownership.rented, fill: '#f59e0b' },
      { name: 'Other', value: stateOverview.building_ownership.other, fill: '#3b82f6' }
    ];

    return (
      <div className="space-y-6">
        {/* Top KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Total AWCs"
            value={stateOverview.total_awc.toLocaleString()}
            unit=""
            icon={Building2}
            color="#3b82f6"
            subtitle={`${stateOverview.total_districts} districts`}
          />
          <KPICard
            title="Own Building Coverage"
            value={stateOverview.building_ownership.own_building_pct}
            unit="%"
            icon={Home}
            color="#22c55e"
            subtitle={`${stateOverview.building_ownership.own_building.toLocaleString()} AWCs`}
          />
          <KPICard
            title="Sanitation Coverage"
            value={stateOverview.sanitation.coverage_pct}
            unit="%"
            icon={Droplets}
            color="#06b6d4"
            subtitle={`${stateOverview.sanitation.not_available} AWCs without`}
          />
          <KPICard
            title="Electricity Coverage"
            value={stateOverview.electricity.coverage_pct}
            unit="%"
            icon={Zap}
            color="#eab308"
            subtitle={`${stateOverview.electricity.not_available} AWCs without`}
          />
        </div>

        {/* Second Row KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Dilapidated AWCs"
            value={stateOverview.dilapidation.dilapidated_total.toLocaleString()}
            unit=""
            icon={AlertTriangle}
            color="#ef4444"
            subtitle="Requiring reconstruction"
          />
          <KPICard
            title="Land Available"
            value={stateOverview.dilapidation.land_available.toLocaleString()}
            unit=""
            icon={MapPin}
            color="#22c55e"
            subtitle="Ready for construction"
          />
          <KPICard
            title="Reconstruction Ready"
            value={stateOverview.dilapidation.reconstruction_ready.toLocaleString()}
            unit=""
            icon={Wrench}
            color="#8b5cf6"
            subtitle="Dilapidated + Land available"
          />
          <KPICard
            title="Rented Buildings"
            value={stateOverview.building_ownership.rented_pct}
            unit="%"
            icon={Building2}
            color="#f59e0b"
            subtitle={`${stateOverview.building_ownership.rented.toLocaleString()} AWCs`}
          />
        </div>

        {/* Building Type Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Building Ownership Distribution</CardTitle>
              <CardDescription>State-level AWC ownership breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <Pie
                    data={buildingTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => {
                      // Only show label if percentage is >= 5% to avoid overlapping
                      if (percent * 100 >= 5) {
                        return `${name}: ${value.toLocaleString()}`;
                      }
                      return '';
                    }}
                    outerRadius={100}
                    innerRadius={0}
                    dataKey="value"
                  >
                    {buildingTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconSize={12}
                    style={{ fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Infrastructure Status Summary</CardTitle>
              <CardDescription>Key infrastructure metrics at a glance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Own Buildings</span>
                  <span className="text-sm font-bold text-green-600">
                    {stateOverview.building_ownership.own_building_pct}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ width: `${stateOverview.building_ownership.own_building_pct}%` }}
                  ></div>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm font-medium">Sanitation Available</span>
                  <span className="text-sm font-bold text-cyan-600">
                    {stateOverview.sanitation.coverage_pct}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-cyan-600 h-2 rounded-full" 
                    style={{ width: `${stateOverview.sanitation.coverage_pct}%` }}
                  ></div>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm font-medium">Electricity Available</span>
                  <span className="text-sm font-bold text-yellow-600">
                    {stateOverview.electricity.coverage_pct}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-600 h-2 rounded-full" 
                    style={{ width: `${stateOverview.electricity.coverage_pct}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Infrastructure Heatmap Preview */}
        <Card>
          <CardHeader>
            <CardTitle>District Infrastructure Heatmap (Top 10)</CardTitle>
            <CardDescription>Composite infrastructure scores by district</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">Own Building %</th>
                    <th className="px-4 py-3 text-center font-semibold">Sanitation %</th>
                    <th className="px-4 py-3 text-center font-semibold">Electricity %</th>
                    <th className="px-4 py-3 text-center font-semibold">Composite Score</th>
                    <th className="px-4 py-3 text-center font-semibold">Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {heatmapData.slice(0, 10).map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district, district_id: district.district });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={district.own_building_pct >= 70 ? 'text-green-600' : 'text-orange-600'}>
                          {district.own_building_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={district.sanitation_pct >= 95 ? 'text-green-600' : 'text-orange-600'}>
                          {district.sanitation_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={district.electricity_pct >= 99 ? 'text-green-600' : 'text-orange-600'}>
                          {district.electricity_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-bold">
                        <span className={
                          district.composite_score >= 80 ? 'text-green-600' : 
                          district.composite_score >= 60 ? 'text-orange-600' : 'text-red-600'
                        }>
                          {district.composite_score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          district.rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                          district.rating === 'Good' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {district.rating}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // PAGE 2: Building Quality & Reconstruction
  const BuildingQualityTab = () => {
    if (!dilapidationData || !buildingOwnership) return <div>No data available</div>;

    const ruralUrbanData = [
      { 
        category: 'Rural', 
        own: buildingOwnership.rural_data[0]?.own_pct || 0,
        rented: buildingOwnership.rural_data[0]?.rented_pct || 0,
        other: buildingOwnership.rural_data[0]?.other_pct || 0
      },
      { 
        category: 'Urban', 
        own: buildingOwnership.urban_data[0]?.own_pct || 0,
        rented: buildingOwnership.urban_data[0]?.rented_pct || 0,
        other: 0
      }
    ];

    return (
      <div className="space-y-6">
        {/* Priority Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-red-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-red-600">{dilapidationData.high_priority.length}</p>
              <p className="text-sm text-gray-600 mt-1">High Priority Districts</p>
              <p className="text-xs text-red-600 mt-2">&gt;200 dilapidated AWCs</p>
            </CardContent>
          </Card>

          <Card className="border-orange-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-orange-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-orange-600">{dilapidationData.medium_priority.length}</p>
              <p className="text-sm text-gray-600 mt-1">Medium Priority Districts</p>
              <p className="text-xs text-orange-600 mt-2">100-200 dilapidated AWCs</p>
            </CardContent>
          </Card>

          <Card className="border-green-500 border-2">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-green-600">{dilapidationData.low_priority.length}</p>
              <p className="text-sm text-gray-600 mt-1">Low Priority Districts</p>
              <p className="text-xs text-green-600 mt-2">&lt;100 dilapidated AWCs</p>
            </CardContent>
          </Card>
        </div>

        {/* Rural vs Urban Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Rural vs Urban Infrastructure Comparison</CardTitle>
            <CardDescription>Building ownership patterns by location type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ruralUrbanData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="own" fill="#22c55e" name="Own Building" radius={[8, 8, 0, 0]} />
                <Bar dataKey="rented" fill="#f59e0b" name="Rented" radius={[8, 8, 0, 0]} />
                <Bar dataKey="other" fill="#3b82f6" name="Other" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Dilapidation Districts */}
        <Card>
          <CardHeader>
            <CardTitle>Top 15 Districts by Dilapidation</CardTitle>
            <CardDescription>Districts requiring urgent reconstruction intervention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">Dilapidated AWCs</th>
                    <th className="px-4 py-3 text-center font-semibold">Land Available</th>
                    <th className="px-4 py-3 text-center font-semibold">Land Not Available</th>
                    <th className="px-4 py-3 text-center font-semibold">Feasibility</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dilapidationData.all_districts.slice(0, 15).map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district_id}</td>
                      <td className="px-4 py-3 text-center font-bold text-red-600">
                        {district.dilapidated_total}
                      </td>
                      <td className="px-4 py-3 text-center text-green-600">
                        {district.land_available_total}
                      </td>
                      <td className="px-4 py-3 text-center text-orange-600">
                        {district.land_not_available_total}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          district.land_available_rural_pct >= 50 ? 'bg-green-100 text-green-800' :
                          district.land_available_rural_pct > 0 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {district.land_available_rural_pct >= 50 ? 'High' : 
                           district.land_available_rural_pct > 0 ? 'Medium' : 'Low'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // PAGE 3: Sanitation & Electricity
  const AmenitiesTab = () => {
    if (!amenitiesData) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        {/* Gap Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-cyan-500 border-2">
            <CardHeader>
              <CardTitle className="text-cyan-600">Sanitation Gap Districts</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-cyan-600 mb-2">
                {amenitiesData.sanitation_gap_districts.length}
              </p>
              <p className="text-sm text-gray-600">Districts below 95% coverage</p>
              <div className="mt-4 space-y-2">
                {amenitiesData.sanitation_gap_districts.slice(0, 5).map((d, idx) => (
                  <div 
                    key={idx} 
                    className="flex justify-between text-sm cursor-pointer hover:bg-cyan-100 p-2 rounded transition-colors"
                    onClick={() => {
                      setSelectedDistrict({ district: d.district_id, district_id: d.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <span className="font-medium">{d.district_id}</span>
                    <span className="text-red-600">{d.sanitation_not_available} AWCs</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-yellow-500 border-2">
            <CardHeader>
              <CardTitle className="text-yellow-600">Electricity Gap Districts</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-yellow-600 mb-2">
                {amenitiesData.electricity_gap_districts.length}
              </p>
              <p className="text-sm text-gray-600">Districts below 99% coverage</p>
              <div className="mt-4 space-y-2">
                {amenitiesData.electricity_gap_districts.slice(0, 5).map((d, idx) => (
                  <div 
                    key={idx} 
                    className="flex justify-between text-sm cursor-pointer hover:bg-yellow-100 p-2 rounded transition-colors"
                    onClick={() => {
                      setSelectedDistrict({ district: d.district_id, district_id: d.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <span className="font-medium">{d.district_id}</span>
                    <span className="text-red-600">{d.electricity_not_available} AWCs</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sanitation Coverage Chart */}
        <Card>
          <CardHeader>
            <CardTitle>District Sanitation Coverage</CardTitle>
            <CardDescription>Sorted by coverage percentage (lowest first)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={amenitiesData.sanitation_records.slice(0, 20)} 
                layout="vertical"
                margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                <YAxis 
                  dataKey="district_id" 
                  type="category"
                  tick={{ fontSize: 10 }}
                  width={120}
                />
                <Tooltip 
                  contentStyle={{ fontSize: '12px' }}
                />
                <Bar 
                  dataKey="sanitation_pct" 
                  radius={[0, 8, 8, 0]}
                  shape={(props) => {
                    const { payload, x, y, width, height } = props;
                    const [isHovered, setIsHovered] = React.useState(false);
                    
                    const handleClick = (e) => {
                      e.stopPropagation();
                      if (payload?.district_id || payload?.district) {
                        const districtId = payload.district_id || payload.district;
                        setSelectedDistrict({ district: districtId, district_id: districtId });
                        setIsModalOpen(true);
                      }
                    };

                    const fillColor = payload?.sanitation_pct >= 95 ? '#22c55e' : '#ef4444';
                    const hoverColor = payload?.sanitation_pct >= 95 ? '#16a34a' : '#dc2626';

                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={isHovered ? hoverColor : fillColor}
                        style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                        onClick={handleClick}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      />
                    );
                  }}
                  cursor="pointer"
                >
                  {amenitiesData.sanitation_records.slice(0, 20).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.sanitation_pct >= 95 ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Electricity Coverage Chart */}
        <Card>
          <CardHeader>
            <CardTitle>District Electricity Coverage</CardTitle>
            <CardDescription>Sorted by coverage percentage (lowest first)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={amenitiesData.electricity_records.slice(0, 20)} 
                layout="vertical"
                margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[98, 100]} tick={{ fontSize: 12 }} />
                <YAxis 
                  dataKey="district_id" 
                  type="category"
                  tick={{ fontSize: 10 }}
                  width={120}
                />
                <Tooltip 
                  contentStyle={{ fontSize: '12px' }}
                />
                <Bar 
                  dataKey="electricity_pct" 
                  radius={[0, 8, 8, 0]}
                  shape={(props) => {
                    const { payload, x, y, width, height } = props;
                    const [isHovered, setIsHovered] = React.useState(false);
                    
                    const handleClick = (e) => {
                      e.stopPropagation();
                      if (payload?.district_id || payload?.district) {
                        const districtId = payload.district_id || payload.district;
                        setSelectedDistrict({ district: districtId, district_id: districtId });
                        setIsModalOpen(true);
                      }
                    };

                    const fillColor = payload?.electricity_pct >= 99 ? '#22c55e' : '#f59e0b';
                    const hoverColor = payload?.electricity_pct >= 99 ? '#16a34a' : '#d97706';

                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={isHovered ? hoverColor : fillColor}
                        style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                        onClick={handleClick}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      />
                    );
                  }}
                  cursor="pointer"
                >
                  {amenitiesData.electricity_records.slice(0, 20).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.electricity_pct >= 99 ? '#22c55e' : '#f59e0b'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Combined Amenities Table */}
        <Card>
          <CardHeader>
            <CardTitle>Combined Amenities Status</CardTitle>
            <CardDescription>Overall sanitation and electricity scores by district</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">Sanitation %</th>
                    <th className="px-4 py-3 text-center font-semibold">Electricity %</th>
                    <th className="px-4 py-3 text-center font-semibold">Overall Score</th>
                    <th className="px-4 py-3 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {amenitiesData.combined_data.slice(0, 20).map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district, district_id: district.district });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={district.sanitation_pct >= 95 ? 'text-green-600' : 'text-red-600'}>
                          {district.sanitation_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={district.electricity_pct >= 99 ? 'text-green-600' : 'text-orange-600'}>
                          {district.electricity_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-bold">
                        {district.overall_score}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          district.overall_score >= 97 ? 'bg-green-100 text-green-800' :
                          district.overall_score >= 95 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {district.overall_score >= 97 ? 'Excellent' :
                           district.overall_score >= 95 ? 'Good' : 'Needs Attention'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // PAGE 4: Micro-Planning & Prioritization
  const PrioritizationTab = () => {
    if (!priorityRanking) return <div>No data available</div>;

    const quadrantData = [
      { name: 'Strong Infrastructure', count: priorityRanking.q1_strong_infrastructure.length, fill: '#22c55e' },
      { name: 'Basic OK, Building Weak', count: priorityRanking.q2_basic_ok_building_weak.length, fill: '#f59e0b' },
      { name: 'High Risk', count: priorityRanking.q3_high_risk.length, fill: '#ef4444' },
      { name: 'Investment Ready', count: priorityRanking.q4_investment_ready.length, fill: '#8b5cf6' }
    ];

    return (
      <div className="space-y-6">
        {/* Quadrant Summary */}
        <Card>
          <CardHeader>
            <CardTitle>District Cluster Categorization</CardTitle>
            <CardDescription>4-quadrant classification for strategic planning</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={quadrantData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Number of Districts', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {quadrantData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Q1: Strong Infrastructure */}
        <Card className="border-green-500 border-2">
          <CardHeader>
            <CardTitle className="text-green-600">Q1: Strong Infrastructure Districts</CardTitle>
            <CardDescription>High sanitation + electricity + own building, low dilapidation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {priorityRanking.q1_strong_infrastructure.slice(0, 8).map((district, idx) => (
                <div 
                  key={idx} 
                  className="text-center p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => {
                    setSelectedDistrict({ district: district.district, district_id: district.district });
                    setIsModalOpen(true);
                  }}
                >
                  <p className="font-semibold text-sm">{district.district}</p>
                  <p className="text-xs text-green-600 mt-1">Score: {district.composite_score}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Q3: High Risk */}
        <Card className="border-red-500 border-2">
          <CardHeader>
            <CardTitle className="text-red-600">Q3: High Risk Districts</CardTitle>
            <CardDescription>Low scores across all parameters - immediate intervention needed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">Composite Score</th>
                    <th className="px-4 py-3 text-center font-semibold">Own Building %</th>
                    <th className="px-4 py-3 text-center font-semibold">Sanitation %</th>
                    <th className="px-4 py-3 text-center font-semibold">Dilapidation %</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {priorityRanking.q3_high_risk.map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-red-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district, district_id: district.district });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district}</td>
                      <td className="px-4 py-3 text-center font-bold text-red-600">
                        {district.composite_score}
                      </td>
                      <td className="px-4 py-3 text-center">{district.own_building_pct}%</td>
                      <td className="px-4 py-3 text-center">{district.sanitation_pct}%</td>
                      <td className="px-4 py-3 text-center text-red-600">
                        {district.dilapidation_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Q4: Investment Ready */}
        <Card className="border-purple-500 border-2">
          <CardHeader>
            <CardTitle className="text-purple-600">Q4: Investment Ready Districts</CardTitle>
            <CardDescription>Dilapidated but land available - quick wins for reconstruction</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {priorityRanking.q4_investment_ready.map((district, idx) => (
                <div 
                  key={idx} 
                  className="p-4 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
                  onClick={() => {
                    setSelectedDistrict({ district: district.district, district_id: district.district });
                    setIsModalOpen(true);
                  }}
                >
                  <p className="font-semibold">{district.district}</p>
                  <p className="text-xs text-purple-600 mt-2">Score: {district.composite_score}</p>
                  <p className="text-xs text-gray-600">Dilapidation: {district.dilapidation_pct}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Complete District Priority Ranking */}
        <Card>
          <CardHeader>
            <CardTitle>Complete District Priority Ranking</CardTitle>
            <CardDescription>All districts sorted by composite infrastructure score</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Rank</th>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">Composite Score</th>
                    <th className="px-4 py-3 text-center font-semibold">Own %</th>
                    <th className="px-4 py-3 text-center font-semibold">Sanitation %</th>
                    <th className="px-4 py-3 text-center font-semibold">Electricity %</th>
                    <th className="px-4 py-3 text-center font-semibold">Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {priorityRanking.all_districts.map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district, district_id: district.district });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium">{district.district}</td>
                      <td className="px-4 py-3 text-center font-bold">
                        <span className={
                          district.composite_score >= 80 ? 'text-green-600' :
                          district.composite_score >= 60 ? 'text-orange-600' :
                          'text-red-600'
                        }>
                          {district.composite_score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{district.own_building_pct}%</td>
                      <td className="px-4 py-3 text-center">{district.sanitation_pct}%</td>
                      <td className="px-4 py-3 text-center">{district.electricity_pct}%</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          district.rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                          district.rating === 'Good' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {district.rating}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              AWC Infrastructure Strengthening Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive monitoring of Anganwadi infrastructure, building quality, and amenities across Gujarat
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="overview">State Overview</TabsTrigger>
              <TabsTrigger value="building-quality">Building Quality</TabsTrigger>
              <TabsTrigger value="amenities">Sanitation & Electricity</TabsTrigger>
              <TabsTrigger value="prioritization">Micro-Planning</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>

            <TabsContent value="building-quality">
              <BuildingQualityTab />
            </TabsContent>

            <TabsContent value="amenities">
              <AmenitiesTab />
            </TabsContent>

            <TabsContent value="prioritization">
              <PrioritizationTab />
            </TabsContent>
          </Tabs>

        </div>
      </div>

      {/* District Detail Modal */}
      <THRDistrictDetailModal
        district={selectedDistrict}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDistrict(null);
        }}
        reportingPeriod="Oct-2025"
        dashboardType="infrastructure"
      />
    </div>
  );
};

export default InfrastructureDashboard;
